import { Links, Meta, Outlet, Scripts, ScrollRestoration, useRouteError, isRouteErrorResponse } from '@remix-run/react';
import type { LinksFunction } from '@remix-run/cloudflare';
import { Toaster } from 'sonner';
import './styles/globals.css';

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  },
];

export default function App() {
  return (
    <html lang="pt-BR" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#2563eb" />
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                if (localStorage.getItem('admin-theme') === 'dark') {
                  document.documentElement.classList.add('dark');
                }
              })();
            `,
          }}
        />
      </head>
      <body>
        <Outlet />
        <Toaster position="top-right" richColors closeButton offset="64px" style={{ zIndex: 9999 }} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return (
    <html lang="pt-BR" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                if (localStorage.getItem('admin-theme') === 'dark') {
                  document.documentElement.classList.add('dark');
                }
              })();
            `,
          }}
        />
      </head>
      <body className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
          <p className="text-sm text-muted-foreground">Carregando...</p>
        </div>
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Detects whether the UI is running in a development build.
 *
 * Remix on Cloudflare Workers does not expose `process.env.NODE_ENV` at
 * runtime in the browser, so we fall back to detecting localhost-style
 * hostnames. In production (real domains) we NEVER render stack traces,
 * error.data, or internal detail strings — doing so leaked source paths,
 * package versions and business logic hints to anyone who could trigger
 * an error (OWASP A01/A04 information disclosure).
 */
function isDevRuntime(): boolean {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') return true;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
  }
  return false;
}

export function ErrorBoundary() {
  const error = useRouteError();
  const isDev = isDevRuntime();

  let title = 'Erro';
  let message = 'Algo deu errado. Tente recarregar a página.';
  let detail = '';

  if (isRouteErrorResponse(error)) {
    title = `${error.status}`;
    message = error.statusText || message;
    if (isDev) {
      detail = typeof error.data === 'string' ? error.data : JSON.stringify(error.data);
    }
  } else if (error instanceof Error) {
    message = isDev ? error.message : 'Erro interno. Nossa equipe foi notificada.';
    if (isDev) {
      detail = error.stack || '';
    } else {
      // Production: emit a structured log (captured server-side by the
      // Worker runtime) instead of leaking the stack to the browser.
      console.error(
        JSON.stringify({
          event: 'admin.error_boundary.uncaught',
          message: error.message,
          name: error.name,
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }

  return (
    <html lang="pt-BR">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center max-w-lg px-4">
          <h1 className="text-4xl font-bold text-foreground">{title}</h1>
          <p className="mt-2 text-muted-foreground">{message}</p>
          {detail && isDev && (
            <pre className="mt-4 text-left text-xs text-muted-foreground bg-muted p-4 rounded-md overflow-auto max-h-48">
              {detail}
            </pre>
          )}
          <a href="/" className="mt-4 inline-block text-primary underline">
            Voltar ao início
          </a>
        </div>
        <Scripts />
      </body>
    </html>
  );
}
